import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, TrendUp, Users, Tag, CheckCircle } from '@phosphor-icons/react';

export default function MarketCard({ market }) {
  const navigate = useNavigate();
  const yesProb = (market.yes_probability * 100).toFixed(0);
  const noProb = (market.no_probability * 100).toFixed(0);
  const isResolved = market.status === 'resolved';

  const daysLeft = Math.max(0, Math.ceil((new Date(market.closes_at) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <div
      onClick={() => navigate(`/market/${market.id}`)}
      className={`bg-[var(--oh-card)] border rounded-xl overflow-hidden hover:border-[var(--oh-border-hover)] transition-all duration-200 hover:scale-[1.02] cursor-pointer flex flex-col relative group ${isResolved ? 'border-[var(--oh-border-hover)] opacity-80' : 'border-[var(--oh-border)]'}`}
      data-testid={`market-card-${market.id}`}
    >
      {/* Category & Country Badge */}
      <div className="px-6 pt-5 pb-0 flex items-center gap-2">
        {isResolved ? (
          <span className="bg-[rgba(245,158,11,0.1)] text-[#F59E0B] border border-[rgba(245,158,11,0.2)] text-xs uppercase tracking-widest px-2 py-1 rounded-sm flex items-center gap-1.5" data-testid={`resolved-badge-${market.id}`}>
            <CheckCircle size={12} weight="fill" />
            Resolved {market.resolution?.toUpperCase()}
          </span>
        ) : (
          <span className="bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border border-[rgba(0,255,163,0.2)] text-xs uppercase tracking-widest px-2 py-1 rounded-sm flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#00FFA3] pulse-dot" />
            Live
          </span>
        )}
        <span className="text-xs text-[var(--oh-text-muted)] uppercase tracking-wider">{market.category}</span>
        <span className="ml-auto text-xs text-[var(--oh-text-secondary)] flex items-center gap-1">
          <Tag size={12} weight="bold" /> {market.country}
        </span>
      </div>

      {/* Question */}
      <div className="px-6 pt-4 pb-3 flex-1">
        <h3 className="font-heading text-base font-medium text-[var(--oh-text)] leading-snug line-clamp-3 group-hover:text-[#00FFA3] transition-colors">
          {market.question}
        </h3>
      </div>

      {/* Probability Bars */}
      <div className="px-6 pb-4 space-y-2">
        <div className="flex gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-[#00FFA3] uppercase tracking-wider">Yes</span>
              <span className="font-mono text-lg font-bold text-[#00FFA3]">{yesProb}%</span>
            </div>
            <div className="h-2 bg-[rgba(0,255,163,0.1)] rounded-full overflow-hidden">
              <div className="h-full bg-[#00FFA3] rounded-full transition-all duration-500" style={{ width: `${yesProb}%` }} />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-[#00B8FF] uppercase tracking-wider">No</span>
              <span className="font-mono text-lg font-bold text-[#00B8FF]">{noProb}%</span>
            </div>
            <div className="h-2 bg-[rgba(0,184,255,0.1)] rounded-full overflow-hidden">
              <div className="h-full bg-[#00B8FF] rounded-full transition-all duration-500" style={{ width: `${noProb}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-[var(--oh-border)] flex items-center justify-between text-xs text-[var(--oh-text-muted)]">
        <div className="flex items-center gap-1">
          <TrendUp size={14} className="text-[#F59E0B]" />
          <span className="font-mono">${(market.total_volume || 0).toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users size={14} />
          <span className="font-mono">{market.trade_count || 0}</span> trades
        </div>
        <div className="flex items-center gap-1">
          <Clock size={14} />
          <span>{daysLeft}d left</span>
        </div>
      </div>
    </div>
  );
}
